<template>
  <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <!-- Overlay -->
    <div class="absolute inset-0 bg-black/40" @click="close" />

    <!-- Panel -->
    <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-dashboard-border">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-emerald-50 rounded-lg">
            <FileSpreadsheet class="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 class="text-lg font-bold text-dashboard-text">Exporter en Excel</h2>
            <p class="text-xs text-dashboard-muted">Choisissez les données à inclure — une feuille par jeu</p>
          </div>
        </div>
        <button @click="close" class="p-1.5 rounded-lg hover:bg-dashboard-bg text-dashboard-muted">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Toolbar -->
      <div class="px-5 py-2.5 border-b border-dashboard-border flex items-center justify-between gap-3 flex-wrap">
        <span class="text-xs text-dashboard-muted">
          <strong class="text-dashboard-text">{{ selectedCount }}</strong> jeu(x) sélectionné(s) · période : {{ periodLabel }}
        </span>
        <div class="flex items-center gap-2">
          <button @click="selectAll(true)" class="text-xs px-2.5 py-1 rounded-md border border-dashboard-border hover:bg-dashboard-bg">Tout cocher</button>
          <button @click="selectAll(false)" class="text-xs px-2.5 py-1 rounded-md border border-dashboard-border hover:bg-dashboard-bg">Tout décocher</button>
          <button @click="resetDefaults" class="text-xs px-2.5 py-1 rounded-md border border-dashboard-border hover:bg-dashboard-bg">Défauts</button>
        </div>
      </div>

      <!-- Groupes -->
      <div class="px-5 py-4 overflow-y-auto space-y-5">
        <div v-for="group in groups" :key="group.name">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm font-semibold text-dashboard-text">{{ group.name }}</h3>
            <label class="text-[11px] text-dashboard-muted flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" :checked="groupAllChecked(group)" @change="onGroupToggle(group, $event)" class="accent-[#22282F]" />
              Tout le groupe
            </label>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label
              v-for="item in group.items"
              :key="item.id"
              :class="['flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors',
                       selected[item.id] ? 'border-[#22282F] bg-[#22282F]/5' : 'border-dashboard-border hover:bg-dashboard-bg']"
            >
              <input type="checkbox" v-model="selected[item.id]" class="mt-0.5 accent-[#22282F]" />
              <div class="min-w-0">
                <span class="text-sm text-dashboard-text">{{ item.label }}</span>
                <span v-if="item.needsLogin" class="ml-1 text-[10px] px-1 py-0.5 rounded bg-sky-50 text-sky-700 align-middle">connexions</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-5 py-4 border-t border-dashboard-border flex items-center justify-between gap-3">
        <p v-if="error" class="text-xs text-red-600">{{ error }}</p>
        <span v-else class="text-xs text-dashboard-muted">Le fichier .xlsx sera téléchargé directement.</span>
        <div class="flex items-center gap-2">
          <button @click="close" class="px-4 py-2 text-sm rounded-lg border border-dashboard-border hover:bg-dashboard-bg">Annuler</button>
          <button
            @click="doExport"
            :disabled="exporting || selectedCount === 0"
            class="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Loader2 v-if="exporting" class="w-4 h-4 animate-spin" />
            <Download v-else class="w-4 h-4" />
            {{ exporting ? 'Génération…' : 'Exporter' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { FileSpreadsheet, X, Download, Loader2 } from 'lucide-vue-next'
import {
  EXPORT_CATALOG, buildAndDownload, fetchLoginAggregates,
  type ExportFilters, type ExportMeta,
} from '@/utils/dashboardExport'

const props = defineProps<{
  open: boolean
  overview: any
  courriers: any
  departs: any
  affectations: any
  params: Record<string, string>
}>()
const emit = defineEmits<{ (e: 'close'): void; (e: 'done', count: number): void }>()

const exporting = ref(false)
const error = ref('')

// État de sélection (id -> bool)
const selected = reactive<Record<string, boolean>>({})
function applyDefaults() {
  for (const item of EXPORT_CATALOG) selected[item.id] = item.default
}
applyDefaults()

const groups = computed(() => {
  const map = new Map<string, typeof EXPORT_CATALOG>()
  for (const item of EXPORT_CATALOG) {
    if (!map.has(item.group)) map.set(item.group, [])
    map.get(item.group)!.push(item)
  }
  return Array.from(map.entries()).map(([name, items]) => ({ name, items }))
})

const selectedCount = computed(() => Object.values(selected).filter(Boolean).length)

function selectAll(v: boolean) {
  for (const item of EXPORT_CATALOG) selected[item.id] = v
}
function resetDefaults() { applyDefaults() }
function groupAllChecked(group: { items: typeof EXPORT_CATALOG }) {
  return group.items.every((i) => selected[i.id])
}
function toggleGroup(group: { items: typeof EXPORT_CATALOG }, v: boolean) {
  for (const i of group.items) selected[i.id] = v
}
function onGroupToggle(group: { items: typeof EXPORT_CATALOG }, e: Event) {
  toggleGroup(group, (e.target as HTMLInputElement).checked)
}

// ── Fenêtre temporelle + filtres depuis les params du dashboard ──
function resolveFilters(): { filters: ExportFilters; periodLabel: string; filtersLabel: string } {
  const p = props.params || {}
  let fromISO: string, toISO: string, periodLabel: string
  if (p.date) {
    fromISO = `${p.date}T00:00:00`; toISO = `${p.date}T23:59:59`; periodLabel = p.date
  } else if (p.from || p.to) {
    const from = p.from || new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10)
    const to = p.to || new Date().toISOString().slice(0, 10)
    fromISO = `${from}T00:00:00`; toISO = `${to}T23:59:59`; periodLabel = `${from} → ${to}`
  } else {
    const to = new Date(); const from = new Date(Date.now() - 30 * 864e5)
    fromISO = from.toISOString(); toISO = to.toISOString(); periodLabel = '30 derniers jours'
  }
  const filters: ExportFilters = {
    fromISO, toISO,
    ambassadeId: p.ambassade ? Number(p.ambassade) : null,
    directionId: p.direction ? Number(p.direction) : null,
    roleCode: p.role || null,
    userId: p.user ? Number(p.user) : null,
  }
  const parts: string[] = []
  if (p.ambassade) parts.push('ambassade')
  if (p.direction) parts.push('direction')
  if (p.role) parts.push(`rôle ${p.role}`)
  if (p.user) parts.push('agent')
  return { filters, periodLabel, filtersLabel: parts.join(', ') }
}

const periodLabel = computed(() => resolveFilters().periodLabel)

const periodLabelRef = ref(periodLabel.value)
watch(() => props.open, (o) => { if (o) periodLabelRef.value = resolveFilters().periodLabel })

async function doExport() {
  if (selectedCount.value === 0) return
  exporting.value = true
  error.value = ''
  try {
    const selectedIds = EXPORT_CATALOG.filter((i) => selected[i.id]).map((i) => i.id)
    const { filters, periodLabel: pl, filtersLabel } = resolveFilters()

    // Charger les agrégats de connexion uniquement si nécessaire
    const needLogin = EXPORT_CATALOG.some((i) => selected[i.id] && i.needsLogin)
    const needActivity = EXPORT_CATALOG.some((i) => selected[i.id] && i.needsActivity)
    const login = needLogin ? await fetchLoginAggregates(filters, needActivity) : null

    const meta: ExportMeta = {
      generatedAt: new Date().toLocaleString('fr-FR'),
      periodLabel: pl,
      filtersLabel,
    }
    const count = await buildAndDownload(selectedIds, {
      overview: props.overview,
      courriers: props.courriers,
      departs: props.departs,
      affectations: props.affectations,
      login,
    }, meta)

    if (count === 0) {
      error.value = 'Aucune donnée à exporter pour la sélection.'
      return
    }
    emit('done', count)
    close()
  } catch (e: any) {
    error.value = e?.message || "Échec de l'export."
  } finally {
    exporting.value = false
  }
}

function close() {
  if (exporting.value) return
  emit('close')
}
</script>
